import { normalizeApiError, PASSWORD_RESET_UNCONFIRMED, type ApiError } from '@bayesprep/shared-ui/errors';
import { ErrorAlert } from '@bayesprep/shared-ui/errors/components';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Lock, Eye, EyeOff, CheckCircle, AlertTriangle, GraduationCap, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { authService } from '@/services/authService';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);

  const [validationUnavailable, setValidationUnavailable] = useState(false);
  const [validationAttempt, setValidationAttempt] = useState(0);

  // Password validation
  const passwordRequirements = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /\d/.test(password),
    hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  useEffect(() => {
    let active = true;
    setTokenValid(null);
    setValidationUnavailable(false);
    setError(''); setFailure(null);
    if (!token) {
      setError('Invalid or missing reset token');
      setTokenValid(false);
      return;
    }
    const validateToken = async () => {
      try {
        await authService.validateResetToken(token);
        if (active) setTokenValid(true);
      } catch (err: any) {
        if (!active) return;
        if (err.code === 'INVALID_RESET_TOKEN') {
          setError('Invalid or expired reset token');
          setTokenValid(false);
        } else {
          setValidationUnavailable(true);
        }
      }
    };
    void validateToken();
    return () => { active = false; };
  }, [token, validationAttempt]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setError(''); setFailure(null);
    setIsLoading(true);

    try {
      await authService.resetPassword(token!, password);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', {
          state: { message: 'Password reset successfully. Please log in with your new password.' }
        });
      }, 3000);
    } catch (err: any) {
      setFailure(normalizeApiError(err, { fallback: PASSWORD_RESET_UNCONFIRMED }));
    } finally {
      setIsLoading(false);
    }
  };

  if (validationUnavailable) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-2xl font-semibold tracking-tight">Reset link validation unavailable</h1>
            <CardDescription role="alert">We couldn't check this link. Your link may still be valid. Retry when the service is available.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full" onClick={() => setValidationAttempt(attempt => attempt + 1)}>Retry validation</Button>
            <Link to="/login" className="block text-center underline">Back to Login</Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (tokenValid === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Invalid Reset Link
              </CardTitle>
              <CardDescription>
                This password reset link is invalid or has expired
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(error || failure) && <ErrorAlert failure={failure} message={error} />}

              <div className="space-y-3">
                <Link to="/forgot-password" className="block">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    Request New Reset Link
                  </Button>
                </Link>

                <Link to="/login" className="block">
                  <Button variant="outline" className="w-full">
                    Back to Login
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <CheckCircle className="h-6 w-6 text-green-700" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Password Reset Successful
              </CardTitle>
              <CardDescription>
                Your password has been updated successfully
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-sm text-gray-600 mb-4">
                You will be redirected to the login page in a few seconds.
              </p>
              <Link to="/login">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  Continue to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card className="shadow-lg">
            <CardContent className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2">Validating reset link...</span>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <GraduationCap className="h-6 w-6 text-blue-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              Reset Password
            </CardTitle>
            <CardDescription>
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {(error || failure) && <ErrorAlert failure={failure} message={error} />}

              <div className="space-y-2">
                <Label htmlFor="password" required>New Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                   aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {/* Password requirements */}
                {password && (
                  <div className="text-xs space-y-1">
                    <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-700' : 'text-red-600'}`}>
                      {passwordRequirements.minLength ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                      At least 8 characters
                    </div>
                    <div className={`flex items-center ${passwordRequirements.hasUppercase ? 'text-green-700' : 'text-red-600'}`}>
                      {passwordRequirements.hasUppercase ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                      One uppercase letter
                    </div>
                    <div className={`flex items-center ${passwordRequirements.hasLowercase ? 'text-green-700' : 'text-red-600'}`}>
                      {passwordRequirements.hasLowercase ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                      One lowercase letter
                    </div>
                    <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-700' : 'text-red-600'}`}>
                      {passwordRequirements.hasNumber ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                      One number
                    </div>
                    <div className={`flex items-center ${passwordRequirements.hasSymbol ? 'text-green-700' : 'text-red-600'}`}>
                      {passwordRequirements.hasSymbol ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                      One special character
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" required>Confirm New Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm your new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isLoading}
                   aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'} aria-pressed={showConfirmPassword} >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>

                {confirmPassword && (
                  <div className={`text-xs flex items-center ${passwordsMatch ? 'text-green-700' : 'text-red-600'}`}>
                    {passwordsMatch ? <CheckCircle2 className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> : <XCircle className="w-3.5 h-3.5 mr-1" aria-hidden="true" />}
                    Passwords match
                  </div>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700"
                disabled={isLoading || !isPasswordValid || !passwordsMatch}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Reset Password
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
